import { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (firebaseUser) => {
    try {
      const tokenResult = await firebaseUser.getIdTokenResult(true);
      const claims = tokenResult.claims;

      let profile = null;
      let orgData = null;

      if (claims.orgId) {
        const userDoc = await getDoc(
          doc(db, 'organisations', claims.orgId, 'users', firebaseUser.uid)
        );
        if (userDoc.exists()) {
          profile = { id: firebaseUser.uid, ...userDoc.data() };
        }
        const orgDoc = await getDoc(doc(db, 'organisations', claims.orgId));
        if (orgDoc.exists()) {
          orgData = { id: claims.orgId, ...orgDoc.data() };
        }
      } else {
        const orgsSnap = await getDocs(collection(db, 'organisations'));
        for (const orgDoc of orgsSnap.docs) {
          const userDoc = await getDoc(
            doc(db, 'organisations', orgDoc.id, 'users', firebaseUser.uid)
          );
          if (userDoc.exists()) {
            profile = { id: firebaseUser.uid, ...userDoc.data() };
            orgData = { id: orgDoc.id, ...orgDoc.data() };
            break;
          }
        }
      }

      setUserProfile(profile);
      setOrg(orgData);

      document.title = orgData?.name ? `Veritas — ${orgData.name}` : 'Veritas';

      if (orgData?.faviconURL) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        link.href = orgData.faviconURL;
        document.head.appendChild(link);
      }

      return { profile, orgData };
    } catch (err) {
      console.error('Auth context error:', err);
      setUserProfile(null);
      setOrg(null);
      document.title = 'Veritas';
      return null;
    }
  };

  // Refresh just the user profile doc without re-fetching org or token
  const refreshProfile = async () => {
    if (!user || !org) return;
    try {
      const userDoc = await getDoc(
        doc(db, 'organisations', org.id, 'users', user.uid)
      );
      if (userDoc.exists()) {
        setUserProfile({ id: user.uid, ...userDoc.data() });
      }
    } catch (err) {
      console.error('refreshProfile error:', err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchProfile(firebaseUser);
      } else {
        setUser(null);
        setUserProfile(null);
        setOrg(null);
        document.title = 'Veritas';
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    userProfile,
    org,
    loading,
    role: userProfile?.role || null,
    orgId: org?.id || userProfile?.orgId || null,
    branchId: userProfile?.branchId || null,
    isAuthenticated: !!user,
    onboardingComplete: userProfile?.onboardingComplete || false,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}